import * as React from 'react';

import { faSidebarFlip } from '@fortawesome/pro-regular-svg-icons';
import { faChevronLeft } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useIntl } from 'react-intl';
import { Link, useParams } from 'react-router-dom';

import SidePanel from 'src/components/sidePanel/SidePanel';
import { ALLOWED_DAYS } from 'src/features/memberships/types';
import DetailsSection from 'src/features/reservations/components/DetailsSection';
import { reservationMessages } from 'src/features/reservations/utils/messages';
import { useGetPrepaidCard } from 'src/features/subscriptions/api/useGetPrepaidCard';
import { useGetPrepaidPlan } from 'src/features/subscriptions/api/useGetPrepaidPlan';
import SubscriptionDetailsSidePanel from 'src/features/subscriptions/components/SubscriptionDetailsSidePanel';
import subscriptionMessages from 'src/features/subscriptions/utils/messages';
import { useMediaQuery } from 'src/hooks/use-media-query';
import { useParking } from 'src/providers/ParkingProvider';
import { useSidePanel } from 'src/registry/providers/SidePanelProvider';
import { Badge } from 'src/registry/ui/Badge';
import { Button } from 'src/registry/ui/Button';
import { Dialog, DialogContent } from 'src/registry/ui/Dialog';
import { useTitleStore } from 'src/stores/useTitleStore';
import { cn } from 'src/utils';
import { generalMessagesDays } from 'src/utils/messages';

const VehicleSubscriptionDetails = () => {
    const { formatMessage, formatDate } = useIntl();
    const { subscriptionId = '' } = useParams();
    const { parkingId } = useParking();
    const { setTitle, removeTitle } = useTitleStore();
    const { setForceSidePanel, openPanel } = useSidePanel();

    const isMobile = useMediaQuery('(max-width: 639px)');

    const { data: subscription } = useGetPrepaidCard({
        parkingId: parkingId,
        cardId: subscriptionId,
    });

    const { data: prepaidPlan } = useGetPrepaidPlan({
        parkingId: parkingId,
        planId: subscription?.purchase_plan_id || '',
        config: { enabled: !!subscription?.purchase_plan_id },
    });

    React.useEffect(() => {
        setTitle(formatMessage(subscriptionMessages.subscription));
        setForceSidePanel(true);
        return () => {
            removeTitle();
            setForceSidePanel(false);
        };
    }, []);

    const additionalPropsForMobile = isMobile && {
        button: {
            title: formatMessage(reservationMessages.actions),
            onClick: () => openPanel('subscription-mobile-side-panel'),
            icon: faSidebarFlip,
        },
    };

    const validMessage = React.useMemo(() => {
        if (!subscription) return '';
        const today = new Date();
        if (today < new Date(subscription.valid_from)) return formatMessage(subscriptionMessages.subscriptionUpcoming);
        if (today < new Date(subscription.valid_until)) return formatMessage(subscriptionMessages.subscriptionActive);
        return formatMessage(subscriptionMessages.subscriptionExpired);
    }, [subscription]);

    return (
        <div>
            <Button asChild className="-mt-6 place-self-start p-0" variant="link">
                <Link to="/vehicles/subscriptions">
                    <FontAwesomeIcon icon={faChevronLeft} className="opacity-50" />
                    {formatMessage(subscriptionMessages.backSubscriptions)}
                </Link>
            </Button>
            <div className="grid">
                <div className="flex flex-col">
                    <div className="grid grid-cols-1 gap-2 xl:grid-cols-2">
                        <DetailsSection
                            title={formatMessage(subscriptionMessages.subscriptionDetails)}
                            data={[
                                {
                                    title: formatMessage(subscriptionMessages.subscriptionNumber),
                                    value: subscription?.card_number,
                                },
                                {
                                    title: formatMessage(subscriptionMessages.subscriptionPlan),
                                    value: prepaidPlan?.plan_name,
                                },
                                {
                                    title: formatMessage(subscriptionMessages.validFrom),
                                    value: formatDate(subscription?.valid_from),
                                },
                                {
                                    title: formatMessage(subscriptionMessages.validTo),
                                    value: formatDate(subscription?.valid_until),
                                },
                                {
                                    title: formatMessage(subscriptionMessages.status),
                                    value: validMessage,
                                },
                            ]}
                            {...additionalPropsForMobile}
                            className="mt-6"
                        />
                        <DetailsSection
                            title={formatMessage(subscriptionMessages.entryRules)}
                            data={[
                                {
                                    title: formatMessage(subscriptionMessages.allowedDays),
                                    value: (
                                        <div className="flex">
                                            {Object.keys(ALLOWED_DAYS).map((day) => (
                                                <Badge
                                                    variant="tag"
                                                    className={cn(
                                                        'mr-1 flex size-6 shrink-0 items-center justify-center bg-white px-0 uppercase opacity-30 last:mr-0',
                                                        subscription?.allowed_days.includes(day as ALLOWED_DAYS) &&
                                                            'font-semibold opacity-100',
                                                    )}
                                                    key={day}
                                                >
                                                    {formatMessage(
                                                        generalMessagesDays[day as keyof typeof generalMessagesDays],
                                                    ).charAt(0)}
                                                </Badge>
                                            ))}
                                        </div>
                                    ),
                                },
                                {
                                    title: formatMessage(subscriptionMessages.entryFrom),
                                    value: subscription?.allowed_hours_start,
                                },
                                {
                                    title: formatMessage(subscriptionMessages.entryUntil),
                                    value: subscription?.allowed_hours_end,
                                },
                            ]}
                            className="mt-6"
                        />
                    </div>
                </div>
                {isMobile ? (
                    <SidePanel name="subscription-mobile-side-panel">
                        <SubscriptionDetailsSidePanel subscription={subscription} isMobile />
                    </SidePanel>
                ) : (
                    <Dialog modal={false} open={true}>
                        <DialogContent variant="sheet" showCloseButton={false}>
                            <SubscriptionDetailsSidePanel subscription={subscription} />
                        </DialogContent>
                    </Dialog>
                )}
            </div>
        </div>
    );
};

export default VehicleSubscriptionDetails;
